import { useCallback, useEffect, useState } from 'react';
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom';
import { Config } from '../../common/config';
import LeftMenu from '../../components/leftMenu';
import useAxiosFetch from '../../hooks/useAxiosFetch';
import useStoreNo from '../../hooks/useStoreNo';
import { isSuccessResponse, getErrorMessage } from '../../components/utils';

/**
 * 상점주 COUPON(목록형) "쿠폰 받은 손님" 화면 — 앱 쿠폰받기로 받은 손님 목록과 발급 취소.
 * 백엔드: admin/promotion/:promotionNo · /coupons · coupon/:no/revoke.
 * 손님은 쿠폰함 QR을 매장에서 보여주고 점주 검증으로 쓴다. 상점 발행 쿠폰이라 정산 보전은 없다.
 * [2026-10-30 B-15] 회원 직접 발급(휴대폰 번호 발급)은 기능 폐지 — 서버 coupon/issue 삭제에 맞춰 화면 코드도 지웠다.
 */
const PAGE_SIZE = 10;
// [2026-10-28] EXPIRED = 기간이 끝난 프로모션의 PENDING (api 가 행 expired 플래그·요약 EXPIRED 로 내려줌)
// const STATUS_LABEL = { PENDING: '미사용', USED: '사용', REVERTED: '환불원복', REVOKED: '발급취소' };
const STATUS_LABEL = { PENDING: '미사용', USED: '사용', REVERTED: '환불원복', REVOKED: '발급취소', EXPIRED: '기간만료' };
const won = n => `${Number(n || 0).toLocaleString()}원`;
// [2026-10-22 L2] 표 칸에서 줄바꿈되던 긴 일시 → "10.21 17:31"
const dt = v => {
  if (!v) return '-';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '-';
  const p = n => String(n).padStart(2, '0');
  return `${p(d.getMonth() + 1)}.${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};
export default function CouponIssue() {
  const navigate = useNavigate();
  const storeNo = useStoreNo();
  const { fetchData } = useAxiosFetch();
  const [searchParams] = useSearchParams();
  const promotionNo = Number(searchParams.get('promotionNo'));

  const [promotion, setPromotion] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [list, setList] = useState([]);
  const [summary, setSummary] = useState({});
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('');

  const base = `${Config.API_SERVER}promotion/${promotionNo}`;

  const loadPromotion = useCallback(async () => {
    const ret = await fetchData({ url: base, method: 'get' });
    // GET promotion/:no 는 { promotion, usedCount, pendingCount } 형태
    if (isSuccessResponse(ret)) setPromotion(ret?.data?.data?.promotion ?? null);
    else setLoadError(getErrorMessage(ret) || '프로모션을 불러오지 못했습니다.');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base]);

  const loadList = useCallback(
    async (p = 1, st = status) => {
      const qs = new URLSearchParams({ page: String(p), size: String(PAGE_SIZE) });
      if (st) qs.set('status', st);
      const ret = await fetchData({ url: `${base}/coupons?${qs.toString()}`, method: 'get' });
      if (!isSuccessResponse(ret)) {
        setList([]);
        return;
      }
      const data = ret?.data?.data ?? {};
      setList(data.list ?? []);
      setSummary(data.summary ?? {});
      setTotal(data.total ?? 0);
      setTotalPages(data.totalPage || 1);
      setPage(p);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [base, status],
  );

  useEffect(() => {
    if (!promotionNo) return;
    loadPromotion();
    loadList(1, '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promotionNo]);

  const handleRevoke = async row => {
    if (!window.confirm(`쿠폰 #${row.promotionCouponNo} 발급을 취소할까요?\n취소하면 손님 쿠폰함에서 사라집니다.`)) return;
    const ret = await fetchData({
      url: `${Config.API_SERVER}promotion/coupon/${row.promotionCouponNo}/revoke`,
      method: 'post',
      data: { reason: '상점주 발급 취소' },
    });
    if (isSuccessResponse(ret)) loadList(page, status);
    else alert(getErrorMessage(ret) || '발급 취소에 실패했습니다.');
  };

  // const issuedCount = (summary.PENDING || 0) + (summary.USED || 0) + (summary.REVERTED || 0);
  const issuedCount = (summary.PENDING || 0) + (summary.EXPIRED || 0) + (summary.USED || 0) + (summary.REVERTED || 0);
  const remain =
    promotion?.totalCount != null ? Math.max(Number(promotion.totalCount) - issuedCount, 0) : null;

  const crumbs = (
    <section className="location_box location_box_mypage">
      <ul className="location_001">
        <li>
          <NavLink to={storeNo ? `/${storeNo}/gift-list` : '/gift-list'}>홈</NavLink>
        </li>
        <li>
          <NavLink to={`/${storeNo}/promotion-list`}>프로모션 관리</NavLink>
        </li>
        <li>쿠폰 받은 손님</li>
      </ul>
    </section>
  );

  if (!promotion) {
    return (
      <>
        {crumbs}
        <main>
          <section className="contants_box contants_box_mypage">
            <LeftMenu pageName="promotion" />
            <section className="rightcontant_box">
              <p>{loadError || '불러오는 중...'}</p>
            </section>
          </section>
        </main>
      </>
    );
  }

  return (
    <>
      {crumbs}
      <main>
        <section className="contants_box contants_box_mypage">
          <LeftMenu pageName="promotion" />
          <section className="rightcontant_box">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h2 className="tit_01" style={{ margin: 0 }}>
                쿠폰 받은 손님 — {promotion.name}
              </h2>
              <button className="button_04 button_gray_line" onClick={() => navigate(`/${storeNo}/promotion-list`)}>
                목록으로
              </button>
            </div>

            <p style={{ fontSize: 13, color: '#666', lineHeight: 1.6, marginBottom: 12 }}>
              쿠폰 1장 {won(promotion.couponDiscountAmount)} · 1인당 {promotion.perUserLimit ?? 1}장
              {remain != null ? ` · 남은 수량 ${remain.toLocaleString()}장` : ' · 수량 제한 없음'}
              <br />
              {/* 발급 현황: 미사용 {summary.PENDING || 0} · 사용 {summary.USED || 0} · 발급취소 {summary.REVOKED || 0} */}
              발급 현황: 미사용 {summary.PENDING || 0}{summary.EXPIRED ? ` · 기간만료 ${summary.EXPIRED}` : ''} · 사용 {summary.USED || 0} · 발급취소 {summary.REVOKED || 0}
              <br />
              손님이 쿠폰함의 QR을 보여주면 점주 검증 후 매장에서 직접 할인해 주세요.
              <br />이 쿠폰은 진행중인 동안 내기프트 앱 <b>쿠폰받기</b>에 노출되고, 손님이 직접 받아 쿠폰함에 담습니다.
            </p>

            {/* [2026-10-28] 받은 쿠폰 목록을 테두리 카드로(상점 정보 관리와 같은 틀) */}
            <article className="form_card list_card" style={{ marginTop: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 12px' }}>
              <h3 style={{ margin: 0 }}>받은 쿠폰 ({total.toLocaleString()}건)</h3>
              <select
                className="select_soft"
                value={status}
                onChange={e => {
                  setStatus(e.target.value);
                  loadList(1, e.target.value);
                }}
              >
                <option value="">전체</option>
                <option value="PENDING">미사용</option>
                <option value="EXPIRED">기간만료</option>
                <option value="USED">사용</option>
                <option value="REVOKED">발급취소</option>
              </select>
            </div>
            {/* [2026-10-22 L3] 구분선 없는 표 → 프로모션 목록과 같은 테두리 표 */}
            <table className="table_box_01">
              <thead>
                <tr>
                  <th>쿠폰번호</th>
                  <th>회원</th>
                  <th>휴대폰</th>
                  <th>상태</th>
                  <th>사용 상점</th>
                  <th>사용일시</th>
                  <th>발급일시</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center' }}>
                      아직 받은 손님이 없습니다.
                    </td>
                  </tr>
                ) : (
                  list.map(r => (
                    <tr key={r.promotionCouponNo}>
                      <td>{r.promotionCouponNo}</td>
                      <td>{r.userName || '-'}</td>
                      <td>{r.mobile || '-'}</td>
                      <td>
                        {r.status === 'USED'
                          ? r.usedVia === 'GIFT'
                            ? '사용(기프트 결제)'
                            : '사용(매장)'
                          : r.expired
                            ? STATUS_LABEL.EXPIRED
                            : STATUS_LABEL[r.status] || r.status}
                      </td>
                      <td>{r.storeName || '-'}</td>
                      <td>{dt(r.usedDttm)}</td>
                      <td>{dt(r.regDttm)}</td>
                      <td>
                        {/* {r.status === 'PENDING' && ( */}
                        {r.status === 'PENDING' && !r.expired && (
                          <button className="button_04 button_gray_line" onClick={() => handleRevoke(r)}>
                            발급취소
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div style={{ display: 'flex', gap: 4, marginTop: 8 }}>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                  <button
                    key={p}
                    className={p === page ? 'button_04 button_black' : 'button_04 button_gray_line'}
                    onClick={() => loadList(p, status)}
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}
            </article>
          </section>
        </section>
      </main>
    </>
  );
}
